import {
  Ban, Blocks, Cloud, Fingerprint, Landmark, Lock, Mail, Scale, Stamp, TriangleAlert,
} from "lucide-react";
import LegalLayout from "../components/legal/LegalLayout";
import ClauseList from "../components/legal/ClauseList";
import { LegalCallout, LegalContact } from "../components/legal/LegalBlocks";

const SECTIONS = [
  {
    id: "ownership",
    title: "Platform Ownership",
    icon: Landmark,
    body: (
      <>
        <p className="lc-lead">
          Thapar Campus Connect is proprietary institutional software. All rights are reserved.
        </p>
        <p>
          The original Campus Connect application code, custom user interface, proprietary workflows, original
          platform content and Campus Connect branding developed for the institution are protected institutional
          assets, subject to any applicable third-party rights.
        </p>
        <LegalCallout>
          This page does not describe a commercial or paid license. Campus Connect is not sold, and no license
          fee, seat count or software-license certificate applies to its users.
        </LegalCallout>
      </>
    ),
  },
  {
    id: "permitted-use",
    title: "Institutional License & Permitted Use",
    icon: Stamp,
    body: (
      <>
        <p>
          Campus Connect is provided for authorized institutional use. Authorized users receive a limited right
          to access and use the deployed platform for legitimate TIET purposes.
        </p>
        <p>This right of use does not transfer ownership of the software or of any part of it.</p>
      </>
    ),
  },
  {
    id: "restrictions",
    title: "Restrictions",
    icon: Ban,
    body: (
      <>
        <p>Without proper authorization, users may not:</p>
        <ClauseList variant="avoid" items={[
          "Copy or redistribute proprietary source code",
          "Sell or commercially license the platform",
          "Create unauthorized derivative deployments",
          "Bypass technical or access restrictions",
          "Reverse engineer proprietary components, except where applicable law permits it",
          "Remove proprietary notices",
          "Represent themselves as the owner of Campus Connect",
        ]} />
      </>
    ),
  },
  {
    id: "third-party",
    title: "Third-Party & Open-Source Software",
    icon: Blocks,
    body: (
      <>
        <p>
          Campus Connect may use open-source software, libraries, frameworks and third-party services. These
          components remain governed by their own copyright notices and licenses.
        </p>
        <LegalCallout title="Third-party licenses are unaffected">
          The Campus Connect proprietary notice does not override the licenses of those components, and nothing
          on this page claims ownership of them.
        </LegalCallout>
      </>
    ),
  },
  {
    id: "branding",
    title: "Institutional Content & Branding",
    icon: Fingerprint,
    body: (
      <p>
        TIET names, marks, logos and official institutional content are subject to institutional ownership and
        usage rules. They may not be used in ways that suggest endorsement or affiliation without permission.
      </p>
    ),
  },
  {
    id: "external-services",
    title: "External Services",
    icon: Cloud,
    body: (
      <p>
        External services that are linked to or integrated with Campus Connect remain governed by the rights and
        terms of their respective providers. Using them is subject to those terms.
      </p>
    ),
  },
  {
    id: "security",
    title: "Security & Unauthorized Use",
    icon: Lock,
    body: (
      <p>
        Access to Campus Connect is intended for authorized users only. Unauthorized use, or attempts to
        interfere with the platform or its protections, may result in restriction of access and, where
        appropriate, action under institutional rules and applicable law.
      </p>
    ),
  },
  {
    id: "disclaimer",
    title: "Disclaimer & Availability",
    icon: TriangleAlert,
    body: (
      <p>
        Campus Connect is provided for institutional operational use. It may evolve, be updated, or be
        temporarily unavailable, and features may change over time. Reasonable care is taken in operating the
        platform, but continuous availability is not guaranteed.
      </p>
    ),
  },
  {
    id: "permissions",
    title: "Permission Requests",
    icon: Mail,
    body: (
      <>
        <p>
          To request permission for any use not covered above, or to ask a question about this page, please
          contact:
        </p>
        <LegalContact />
      </>
    ),
  },
  {
    id: "copyright",
    title: "Copyright Notice",
    icon: Scale,
    body: (
      <div className="lc-copyright">
        <strong>© 2026 Thapar Institute of Engineering &amp; Technology.</strong>
        Thapar Campus Connect. All rights reserved, subject to applicable third-party licenses.
      </div>
    ),
  },
];

export default function LicensePage() {
  return (
    <LegalLayout
      current="license"
      title="Software License & Intellectual Property"
      subtitle="Usage and intellectual-property terms for the Thapar Campus Connect digital platform."
      sections={SECTIONS}
    />
  );
}
